import { z } from "zod";

/**
 * Provider-neutral LLM interface. Production callers wire the Anthropic SDK behind this;
 * tests/CI wire a RecordedLlmClient that replays a JSON fixture for determinism.
 *
 * ADR-0003: deliberately abstract so we don't take a hard vendor lock-in.
 */
export const LlmMessageSchema = z.object({
  role: z.enum(["system", "user", "assistant", "tool"]),
  content: z.string()
});
export type LlmMessage = z.infer<typeof LlmMessageSchema>;

/** Tool a model may invoke (provider-neutral). */
export const LlmToolSpecSchema = z.object({
  name: z.string().min(1),
  description: z.string(),
  /** JSON Schema for the tool's input arguments. */
  inputSchema: z.unknown()
});
export type LlmToolSpec = z.infer<typeof LlmToolSpecSchema>;

export const LlmRequestSchema = z.object({
  model: z.string().min(1),
  messages: z.array(LlmMessageSchema).min(1),
  temperature: z.number().min(0).max(2).default(0),
  maxTokens: z.number().int().positive().default(2048),
  /** Optional structured-output JSON Schema constraint. */
  responseFormat: z.unknown().optional(),
  /** Tools the model may call (Phase 12.2 tool-use loop). */
  tools: z.array(LlmToolSpecSchema).optional()
});
export type LlmRequest = z.infer<typeof LlmRequestSchema>;

export const LlmResponseSchema = z.object({
  text: z.string(),
  /** Raw tool call(s), if any, in provider-neutral shape. */
  toolCalls: z
    .array(
      z.object({
        name: z.string(),
        args: z.unknown()
      })
    )
    .default([]),
  usage: z
    .object({
      inputTokens: z.number().int().nonnegative(),
      outputTokens: z.number().int().nonnegative()
    })
    .optional()
});
export type LlmResponse = z.infer<typeof LlmResponseSchema>;

export interface LlmClient {
  complete(req: LlmRequest): Promise<LlmResponse>;
}

/**
 * RecordedLlmClient — deterministic playback. Keyed by canonical hash of the request.
 * If a transcript is missing for a request, throws — forcing the test author to
 * either re-record or add the fixture by hand.
 */
export class RecordedLlmClient implements LlmClient {
  constructor(private readonly transcripts: Map<string, LlmResponse>) {}

  static fromArray(items: { keyHash: string; response: LlmResponse }[]): RecordedLlmClient {
    const m = new Map<string, LlmResponse>();
    for (const { keyHash, response } of items) m.set(keyHash, response);
    return new RecordedLlmClient(m);
  }

  async complete(req: LlmRequest): Promise<LlmResponse> {
    const { canonicalSha256, canonicalJson } = await import("./digest.js");
    const key = canonicalSha256(req);
    const r = this.transcripts.get(key);
    if (!r) {
      // Include the canonical JSON envelope so the test author can see what to record.
      // Truncated to 2k chars to keep the error readable.
      const envelope = canonicalJson(req);
      const preview = envelope.length > 2000 ? `${envelope.slice(0, 2000)}…[truncated]` : envelope;
      throw new Error(
        `RecordedLlmClient: no transcript for request key ${key}.\n` +
          `Request envelope: ${preview}\n` +
          `Add a fixture { keyHash: "${key}", response: {...} } or rerun in record mode.`
      );
    }
    return r;
  }
}
